<template>
  <b-card class="bg-light" :class="{ 'border-success': isNew }">
    <div slot="header">
      <b-row>
        <b-col vcols="12">
          <span v-if="isNew" >{{ 'add_new_item' | translate('global') }}</span>
          <span v-else>{{ 'edit_item' | translate('global') }}</span>
        </b-col>
      </b-row>
    </div>
    <b-row>
      <b-col cols="12" xl="4">
        <b-form-group :label="'product_service' | translate('global')" label-class="h-40-px">
          <treeselect value-format="object" :default-expand-level="1" :disabled="!actions.update"
                      :placeholder="'select_product_service' | translate('global')"
                      instanceId="loadProducts" :class="{ 'treeselect-error' : errorMsg.product_id }"
                      :load-options="loadOptions" :async="true" :cacheOptions="false"
                      :searchPromptText="'product_service_autocomplete' | translate('global')"
                      :noResultsText="'no_results_found' | translate('global')"
                      v-model="selectedProduct"/>
          <span class="error-text" v-if="errorMsg.product_id">
                {{ errorMsg.product_id[0] }}
              </span>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="2">
        <b-form-group :label="'amount' | translate('global')" label-class="h-40-px">
          <b-form-input type="number" min="0" :disabled="!actions.update"
                        :class="{ 'is-invalid' : errorMsg.quantity }"
                        :placeholder=" 'add_amount' | translate('global')"
                        v-model="item.quantity"/>
          <div class="invalid-feedback" v-if="errorMsg.quantity">{{ errorMsg.quantity[0] }}</div>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="2">
        <b-form-group :label="'unit' | translate('global')" label-class="h-40-px">
          <treeselect value-format="object" :disabled="!actions.update"
                      :class="{ 'treeselect-error' : errorMsg.units_classifier_id }"
                      :placeholder=" 'select_unit' | translate('global') "
                      :options="unitOptions" v-model="selectedUnit"/>
          <span class="error-text" v-if="errorMsg.units_classifier_id">
                {{ errorMsg.units_classifier_id[0] }}
              </span>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="2">
        <b-form-group :label="'unit_price' | translate('global')" label-class="h-40-px">
          <b-form-input type="number" min="0" :disabled="!actions.update"
                        :class="{ 'is-invalid' : errorMsg.price }"
                        :placeholder=" 'add_unit_price' | translate('global')"
                        v-model="item.price"/>
          <div class="invalid-feedback" v-if="errorMsg.price">{{ errorMsg.price[0] }}</div>
        </b-form-group>
      </b-col>
      <b-col cols="6" xl="2">
        <b-form-group :label="'sum' | translate('global')" label-class="h-40-px">
          <b-form-input disabled :value="(item.quantity * item.price || 0).toFixed(2)"/>
        </b-form-group>
      </b-col>
      <b-col cols="12" xl="12">
        <b-form-group :label="'order_purpose' | translate('global')">
          <b-form-textarea :placeholder=" 'add_order_purpose' | translate('global') " rows="1" max-rows="3"
                           :disabled="!actions.update" :class="{ 'is-invalid' : errorMsg.purpose }"
                           v-model="item.purpose"/>
          <div class="invalid-feedback" v-if="errorMsg.purpose">{{ errorMsg.purpose[0] }}</div>
        </b-form-group>
      </b-col>
    </b-row>

    <hr>

    <b-row>
      <b-col cols="6" xl="4">
        <b-form-group :label="'warehouse' | translate('global')">
          <treeselect value-format="object" 
                      :disabled="!actions.update || (selectedProduct && parseInt(selectedProduct.type) === 1)"
                      :class="{ 'treeselect-error' : errorMsg.warehouses_classifier_id }"
                      :placeholder=" 'select_warehouse' | translate('global') "
                      :options="warehouseOptions" v-model="selectedWarehouse"/>
          <span class="error-text" v-if="errorMsg.warehouses_classifier_id">
            {{ errorMsg.warehouses_classifier_id[0] }}
          </span>
        </b-form-group>
      </b-col>
      <b-col cols="12" xl="4">
        <b-form-group :label="'transport' | translate('global')">
          <treeselect value-format="object" 
                      :disabled="!actions.update || (selectedProduct && parseInt(selectedProduct.type) === 0)"
                      :class="{ 'treeselect-error' : errorMsg.transports_classifier_id }"
                      :placeholder=" 'select_transport' | translate('global') "
                      :options="transportOptions" v-model="selectedTransport"/>
          <span class="error-text" v-if="errorMsg.transports_classifier_id">
            {{ errorMsg.transports_classifier_id[0] }}
          </span>
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-form-group :label="'delivery_transport_included' | translate('global')">
          <b-form-radio-group class="mt-2" :options="deliveryTransportIncludedOptions"
                              :disabled="!actions.update"
                              v-model="item.is_delivery_transport" />
        </b-form-group>
      </b-col>
    </b-row>
    <div slot="footer" class="pull-right flex-container">
      <b-button type="submit" variant="success" :disabled="spinners.isSaving || !actions.update" @click="onSave()">
        <b-spinner v-if="spinners.isSaving" type="grow" small/>
        {{ 'save' | translate('global') }}
      </b-button>
      <b-button type="reset" variant="secondary" class="ml-2" @click="onCancel()">
        {{ 'cancel' | translate('global') }}
      </b-button>
      <DeleteItem v-if="!isNew" class="ml-2" :itemId="data.id" :isDisabled="!actions.delete"/>
    </div>
  </b-card>
</template>

<script>
import VuexTypes from '@/store/types/index';
import { mapGetters, mapActions } from 'vuex';
import statuses from '@/store/statuses';
import Services from '@/services/index';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import forEach from 'lodash.foreach';
import DeleteItem from './DeleteItem.vue';

const OrdersTypes = VuexTypes.orders;
const OrdersService = Services.orders;

export default {
  mounted() {
    this.fillClassifiers();
  },
  mixins: [TreeselectMixin],
  props: ['data', 'isNew'],
  data() {
    return {
      isItems: null,
      spinners: {
        isSaving: false,
      },
      actions: {},
      item: {
        product_id: null,
        quantity: null,
        units_classifier_id: null,
        price: null,
        warehouses_classifier_id: null,
        transports_classifier_id: null,
        is_delivery_transport: null,
        contracts_classifier_id: null,
        purpose: '',
      },
      selectedProduct: null,
      selectedUnit: null,
      selectedWarehouse: null,
      selectedTransport: null,
      unitOptions: [],
      siteOptions: [],
      warehouseOptions: [],
      transportOptions: [],
      errorMsg: {},
      deliveryTransportIncludedOptions: [
        { text: this.$t('no', 'global'), value: 0 },
        { text: this.$t('yes', 'global'), value: 1 },
      ],
    };
  },
  components: { Treeselect, DeleteItem },
  computed: {
    ...mapGetters('Orders', [
      'orderCreateItem',
      'orderUpdateItem',
      'orderUpdateData',
      'ordersClassifiers',
      'orderItems'
    ]),
    isDeliveryTransport() {
      return this.item['is_delivery_transport'];
    }
  },
  watch: {
    data: {
      immediate: true,
      handler: function () {
        this.errorMsg = {};
        if (this.data) {
          this.fillData(this.data);
        }
      },
      deep: true
    },
    ordersClassifiers: {
      handler: function () {
        if (this.ordersClassifiers.status === statuses.LOADED) {
          this.fillClassifiers();
        }
      },
      deep: true
    },
    orderItems: {
      immediate: true,
      handler: function () {
        if (this.orderItems.status === statuses.LOADED) {
          this.actions = this.orderItems.data.actions;
        }
      },
      deep: true
    },
    orderCreateItem: {
      handler: function () {
        this.errorMsg = {};
        this.spinners.isSaving = false;
        if (this.orderCreateItem.status === statuses.LOADED) {
          this.fillData(this.orderCreateItem.data);
          this.alertSuccess();
        } else if (this.orderCreateItem.status === statuses.ERROR) {
          this.alertError();
          this.errorMsg = this.orderCreateItem.data.data.errors;
        }
      },
      deep: true
    },
    orderUpdateItem: {
      handler: function () {
        this.errorMsg = {};
        this.spinners.isSaving = false;
        if (this.orderUpdateItem.status === statuses.ERROR) {
          this.alertError();
          this.errorMsg = this.orderUpdateItem.data.data.errors;
        }
      },
      deep: true
    },
    selectedProduct: function () {
      this.item['product_id'] = this.selectedProduct ? this.selectedProduct['id'] : null;
      if (this.selectedProduct) {
        if (this.data['product'] && this.selectedProduct.id !== this.data['product'].id) {
          this.selectedWarehouse = null;
          this.selectedTransport = null;
          this.item['is_delivery_transport'] = 0;
        }

        if (!this.data['product'] || (this.data['product'] && this.selectedProduct.id !== this.data['product'].id)) {
          this.selectedUnit = this.selectedProduct && this.selectedProduct['units_classifier'] ? {
            id: this.selectedProduct['units_classifier']['id'],
            label: this.selectedProduct['units_classifier']['name']
          } : null;
        }

        if (this.selectedProduct.is_transport_required && this.selectedProduct.transports_classifier) {
          this.selectedTransport = {
            id: this.selectedProduct['transports_classifier']['id'],
            label: this.selectedProduct['transports_classifier']['name']
          };
        }
      }
    },
    isDeliveryTransport: {
      handler: function () {
        this.selectedTransport = this.isDeliveryTransport === 0 ? null : this.selectedTransport;
      },
      deep: true
    },
    selectedUnit: function () {
      this.item['units_classifier_id'] = this.selectedUnit ? this.selectedUnit['id'] : null;
    },
    selectedWarehouse: function () {
      this.item['warehouses_classifier_id'] = this.selectedWarehouse ? this.selectedWarehouse['id'] : null;
    },
    selectedTransport: function () {
      this.item['transports_classifier_id'] = this.selectedTransport ? this.selectedTransport['id'] : null;
    }
  },
  methods: {
    ...mapActions('Orders', [
      OrdersTypes.ORDER_CREATE_ITEM,
      OrdersTypes.ORDER_UPDATE_ITEM,
    ]),
    fillData(data) {
      this.item.product_id = data.product ? data.product.id : null;
      this.item.quantity = data.quantity;
      this.item.units_classifier_id = data.units_classifier ? data.units_classifier.id : null;
      this.item.price = data.price;
      this.item.warehouses_classifier_id = data['warehouses_classifier'] ? data['warehouses_classifier']['id'] : null;
      this.item.transports_classifier_id = data['transports_classifier'] ? data['transports_classifier']['id'] : null;
      this.item.is_delivery_transport = data.is_delivery_transport ? 1 : 0;
      this.item.contracts_classifier_id = data['contracts_classifier'] ? data['contracts_classifier']['id'] : null;
      this.item.purpose = data.purpose;

      this.selectedProduct = data['product'] ? {
        id: data['product']['id'],
        label: data['product']['full_name'],
        type: data['product']['type'],
        is_transport_required: data['product']['is_transport_required'],
        transports_classifier: data['product']['transports_classifier'] ? {
          id: data['product']['transports_classifier']['id'],
          name: data['product']['transports_classifier']['ax_code']
        } : null,
        units_classifier: data['units_classifier'] ? {
          id: data['units_classifier']['id'],
          name: data['units_classifier']['name']
        } : null
      } : null;

      this.selectedUnit = data['units_classifier'] ? {
        id: data['units_classifier']['id'],
        label: data['units_classifier']['name']
      } : null;

      this.selectedWarehouse = data['warehouses_classifier'] ? {
        id: data['warehouses_classifier']['id'],
        label: data['warehouses_classifier']['name']
      } : null;

      this.selectedTransport = data['transports_classifier'] ? {
        id: data['transports_classifier']['id'],
        label: data['transports_classifier']['ax_code']
      } : null;
    },
    fillClassifiers() {
      this.unitOptions = this.fillTreeselectDropdown(
        this.ordersClassifiers.data['unitsClassifier'], 'id', 'name'
      );
      this.siteOptions = this.fillTreeselectDropdown(
        this.ordersClassifiers.data['sitesClassifier'], 'id', 'name', ['warehousesClassifier']
      );
      this.transportOptions = this.fillTreeselectDropdown(
        this.ordersClassifiers.data['transportsClassifier'], 'id', 'ax_code'
      );
      const sitesClassifier = this.ordersClassifiers.data['sitesClassifier'];

      const selectedSite = this.orderUpdateData.data.order['sitesClassifier']
        ? this.orderUpdateData.data.order['sitesClassifier']['id'] : null;

      if (selectedSite) {
        let warehousesClassifier = [];
        forEach(sitesClassifier, (item) => {
          if (item['id'] === selectedSite) {
            warehousesClassifier = item['warehousesClassifier'];
          }
        });
        if (warehousesClassifier) {
          this.warehouseOptions = this.fillTreeselectDropdown(warehousesClassifier, 'id', 'name');
        }
      }
    },
    onSave() {
      this.spinners.isSaving = true;
      if (this.isNew) {
        this.ORDER_CREATE_ITEM({
          orderId: this.$route.params.id,
          createData: this.item
        });
      } else {
        this.ORDER_UPDATE_ITEM({
          orderId: this.$route.params.id,
          itemId: this.data.id,
          updateData: this.item
        });
      }
    },
    onCancel() {
      this.$emit('cancel');
    },
    async loadOptions({ action, searchQuery, callback, instanceId }) {
      let options = [];
      const query = searchQuery.trim();
      if (action === 'ASYNC_SEARCH') {
        const valid = this.checkSearchQuery(query);
        if (valid) {
          options = await this[instanceId](query);
        }
      }
      callback(null, options);
    },
    async loadProducts(name) {
      const param = name || '';
      const data = await OrdersService.searchProduct(param);
      const options = [];
      forEach(data.data.data, (item) => {
        options.push({
          id: item['id'],
          label: item['name'],
          type: item['type'],
          is_transport_required: item['is_transport_required'],
          transports_classifier: item['transports_classifier'] ? {
            id: item['transports_classifier']['id'],
            name: item['transports_classifier']['ax_code']
          } : null,
          units_classifier: item['units_classifier'] ? {
            id: item['units_classifier']['id'],
            name: item['units_classifier']['name']
          } : null
        });
      });
      return options;
    },
  }
};

</script>

<style scoped>
</style>
